import { Button, Typography } from 'omakase';
import { CircularProgress } from '@material-ui/core';
import { FunctionComponent, useState } from 'react';
import { IconLocationFilled } from '@tock/icons';
import {
  PatronLocationEntity,
  geocodeResultToPatronLocation,
} from 'components/patron-location/PatronLocation';
import { currentLocationButton } from 'components/patron-location/patronLocationCssDefs';
import { getGeocodedLocation, responsiblyGetCurrentPosition } from 'utils/locationUtils';
import { useActions } from '@tock/shared/utils/connect';

interface PatronLocationFinderCurrentPositionProps {
  onResult(result: PatronLocationEntity): void;
}

export const PatronLocationFinderCurrentPosition: FunctionComponent<
  PatronLocationFinderCurrentPositionProps
> = ({ onResult }) => {
  const [locating, setLocating] = useState(false);
  const { actions } = useActions();

  // Must always be called as a result of a user action
  const getPosition = async () => {
    try {
      setLocating(true);
      const coordinates = await responsiblyGetCurrentPosition(true);
      if (coordinates && coordinates.latitude && coordinates.longitude) {
        const result = await getGeocodedLocation(coordinates.latitude, coordinates.longitude);
        const item = await geocodeResultToPatronLocation(
          result[0],
          actions.metroArea.findClosestMetroArea
        );
        onResult(item);
      }
      setLocating(false);
    } catch (error) {
      setLocating(false);
      // A user declining the geolocation permission is a normal outcome, not a
      // failure worth logging or toasting.
      const permissionDenied =
        typeof GeolocationPositionError !== 'undefined' &&
        error instanceof GeolocationPositionError &&
        error.code === GeolocationPositionError.PERMISSION_DENIED;
      if (permissionDenied) {
        return;
      }
      const cause = error instanceof Error ? error : new Error(String(error));
      window.LOGGER?.error('Failed to resolve current position', cause);
      actions.toast.showError("We couldn't find your current location. Please try again.");
    }
  };

  const disabled = locating;

  const primaryText = (
    <Typography color="inherit" variant="body01Strong" component="span" tabIndex={-1}>
      Use current location
    </Typography>
  );

  return (
    <Button
      iconStart={disabled ? <CircularProgress color="inherit" size={20} /> : <IconLocationFilled />}
      variant="basic"
      size="large"
      fullWidth
      disabled={disabled}
      onClick={getPosition}
      css={currentLocationButton}
    >
      {primaryText}
    </Button>
  );
};
